import React, { useCallback, useState, useContext } from 'react'
import { VisuallyHidden } from '../../a11y/visually-hidden'
import { VideoPopup } from './video-popup'
import { useIsInternalTesting } from '../../hooks/use-internal-testing'
import { SectionContext } from '../../../../contexts/section-context'
import { PageContext } from '../../../../contexts/page-context'
import { WatchFullVideo } from '../../icons/watch-full-video'
import { CircularPlay } from '../../icons/circular-play'
import { IconButton } from '../../button'
import { gnarSpec } from '../../../../lib/tracking/gnar'
import cn from 'classnames'

import styles from './youtube-embed-popup.module.scss'
import { useIsSectionThemeDark } from '@contexts/section-theme-context'
import { useIntl } from 'react-intl'

interface VideoEmbedPopupProps {
  youtubeId: string
  videoInternalName: string
  title?: string
  videoDescription?: string
  thumbnail?: string
  dropShadow?: boolean
  autoPlay?: boolean
}

const getYoutubeEmbedUrl = (youtubeId: string): string => `https://www.youtube.com/embed/${youtubeId}`

export const YoutubeEmbedPopup: React.FC<VideoEmbedPopupProps> = ({
  youtubeId,
  videoInternalName,
  title = `YouTube video player - ${youtubeId}`,
  videoDescription,
  thumbnail,
  dropShadow,
  autoPlay,
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const isInternalTesting = useIsInternalTesting()
  const { isHero } = useContext(SectionContext)
  const { pageId, slug } = useContext(PageContext)
  const isDarkTheme = useIsSectionThemeDark()

  const onShowFullVideo = () => {
    setIsPopupOpen(true)
    void gnarSpec.watchFullVideoButtonClickButtonClick(pageId, slug, videoInternalName)
  }

  const hidePopup = useCallback(() => {
    setIsPopupOpen(false)
  }, [])

  // setting playlist param to youtube ID is how to loop an iframe youtube embed: https://developers.google.com/youtube/player_parameters
  const autoplay = isHero && !isInternalTesting && autoPlay ? 1 : 0
  const youtubeParams = `?controls=0&mute=1&autoplay=${autoplay}&loop=1&playlist=${youtubeId}`
  const embedUrl = getYoutubeEmbedUrl(youtubeId)
  const fullYoutubeUrl = `${embedUrl}${youtubeParams}`
  const intl = useIntl()

  return (
    <div className={styles.videoEmbedWrapper} data-component-id={videoInternalName}>
      {isPopupOpen && (
        <VideoPopup
          autoplay
          url={embedUrl}
          isOpen={isPopupOpen}
          hidePopup={hidePopup}
          title={intl.formatMessage(
            {
              id: 'video.youtubeEmbedPopup.videoPopup.title',
              description: 'Video - Youtube Embed Popup - Video Popup - Title',
              defaultMessage: '{title} - full video',
            },
            { title }
          )}
        />
      )}
      {/**
       * When a thumbnail doesn't exist for a video, fallback to iframe to accommodate
       * the addition of thumbnails to each youtube video asset in Contentful
       * TODO: Clean up after thumbnail assets have been added for youtube videos.
       */}
      {(isHero && autoplay) || !thumbnail ? (
        <>
          <iframe
            src={fullYoutubeUrl}
            className={cn(styles.iframeEmbedStyle, dropShadow && styles.dropShadow)}
            title={title}
            allow="autoplay; loop"
            allowFullScreen
          />
          <IconButton
            className={styles.showFullVideoButtonStyle}
            onClick={onShowFullVideo}
            aria-label={intl.formatMessage({
              id: 'video.youtubeEmbedPopup.playButton.ariaLabel',
              description: 'Video - Youtube Embed Popup - Play Button - Aria Label',
              defaultMessage: 'Watch Full Video',
            })}
          >
            <WatchFullVideo className={styles.watchFullVideoIcon} />
          </IconButton>
        </>
      ) : (
        <button
          aria-label={intl.formatMessage({
            id: 'video.youtubeEmbedPopup.watchVideo.ariaLabel',
            description: 'Video - Youtube Embed Popup - Watch Video - Aria Label',
            defaultMessage: 'Watch Full Video',
          })}
          className={cn(styles.thumbnailContainer, isDarkTheme && styles.darkTheme)}
          onClick={onShowFullVideo}
        >
          {thumbnail && (
            <img
              src={thumbnail}
              alt={intl.formatMessage(
                {
                  id: 'video.youtubeEmbedPopup.watchVideo.image.altText',
                  description: 'Video - Youtube Embed Popup - Watch Video - Image - Alt Text',
                  defaultMessage: 'Play Video - {title}',
                },
                { title }
              )}
            />
          )}
          <CircularPlay className={styles.thumbnailPlayButtonStyle} />
        </button>
      )}

      {videoDescription && <VisuallyHidden>{videoDescription}</VisuallyHidden>}
    </div>
  )
}
